import type { ReactNode } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useEntity } from '../../contexts/EntityContext';
import { trpc } from '../../lib/trpc';
import { locatedPath } from './itemLinks';

/**
 * A link to an item listed on a page scoped to one ownership. The server says
 * where the item lives; following the link chooses that ownership in the header
 * first, so the page lists the item. Until the server answers, or when the
 * person cannot read the item, it shows the item's name only.
 */
export function LocatedItemLink({
  displayId,
  children,
}: {
  displayId: string;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { selectedEntity, setSelectedEntity } = useEntity();
  const location = useQuery({
    ...trpc.agentChat.locateItem.queryOptions({ displayId }),
    retry: false,
    staleTime: Infinity,
  });
  if (!location.data) return <>{children}</>;

  const { ownershipId } = location.data;
  const chooseOwnership = () => {
    if (selectedEntity?.type === 'ownership' && selectedEntity.ownershipId === ownershipId) return;
    const ownership = queryClient
      .getQueryData(trpc.entities.getEntities.queryKey())
      ?.find((entity) => entity.type === 'ownership' && entity.ownershipId === ownershipId);
    if (ownership) setSelectedEntity(ownership);
  };
  return (
    <Link to={locatedPath(location.data)} onClick={chooseOwnership} data-testid="agent-item-link">
      {children}
    </Link>
  );
}
